// src/contexts/ThemeContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { storage, STORAGE_KEYS } from '@/services/storage';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
  setTheme: (dark: boolean) => void;
  colors: {
    background: string;
    card: string;
    text: string;
    border: string;
    primary: string;
  };
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Load saved preference on mount, also respect OS preference as default
  useEffect(() => {
    const load = async () => {
      const saved = await storage.getItem(STORAGE_KEYS.THEME);
      if (saved === 'dark') {
        setIsDarkMode(true);
      } else if (saved === 'light') {
        setIsDarkMode(false);
      } else {
        // No saved preference — use OS preference
        setIsDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
      }
    };
    load();
  }, []);

  // Apply/remove 'dark' class on <html>
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleTheme = useCallback(() => {
    setIsDarkMode(prev => {
      const next = !prev;
      storage.setItem(STORAGE_KEYS.THEME, next ? 'dark' : 'light');
      return next;
    });
  }, []);

  const setTheme = useCallback((dark: boolean) => {
    setIsDarkMode(dark);
    storage.setItem(STORAGE_KEYS.THEME, dark ? 'dark' : 'light');
  }, []);

  const colors = {
    background: isDarkMode ? '#0a0a0a' : '#F8FAFC',
    card:       isDarkMode ? '#0c0c0c' : '#FFFFFF',
    text:       isDarkMode ? '#E5E5E5' : '#0F172A',
    border:     isDarkMode ? '#ffc800' : '#E2E8F0',
    primary:    '#4F46E5',
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme, setTheme, colors }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
};
